import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { navigationItems } from "../../../lib/definitions";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaChevronLeft } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { FaChevronRight } from "react-icons/fa6";
import { FaLongArrowAltLeft } from "react-icons/fa";

export default function Sidebar({ isOpen, toggleSidebar }) {
  const userPermissions = JSON.parse(localStorage.getItem("permissions")) || [] ;  

  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const [dropdownsOpen, setDropdownsOpen] = useState({});

  const toggleDropdown = (key) => {
    setDropdownsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredNavigationItems = navigationItems
    .map((item) => {
      if (item.subItems) {
        const subItems = item.subItems.filter((sub) =>
          userPermissions.includes(sub.permissions)
        );
        if (subItems.length > 0 || userPermissions.includes(item.permissions)) {
          return { ...item, subItems }
        }
        return null;
      }
      return userPermissions.includes(item.permissions) ? item : null;
    })
    .filter(Boolean);

    const groupedItems = filteredNavigationItems.reduce((acc, item) => {
        const section = item.section || "Other";
        acc[section] = acc[section] || [];
        acc[section].push(item);
        return acc;
    }, {});

  return (
    <div className="h-screen fixed z-50 shadow-2xl">
      <motion.div
        animate={{
          width: isOpen ? "270px" : "80px",
          boxShadow: "4px 0 15px rgba(0, 0, 0, 0.1)",
        }}
        className="bg-primary h-screen max-h-screen p-2 fixed overflow-y-auto transition-[width]  sidebar-scroll"
        style={{ backdropFilter: "blur(10px)" }}
        >
        {/* Logo + Toggle */}
        <div className="flex items-center justify-center gap-9 pt-6 pb-3">
          {isOpen && (
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`text-[30px] font-bold ${currentLang === "ar" ? "right-3" : "left-3"} relative text-[#2F7A74] tracking-wider`}
            >
              WASLY
            </motion.span>
          )}
          {isOpen ?
            <FaLongArrowAltLeft className={`top-3 ${currentLang === "ar" ? "left-2" : "right-2"}  w-5 h-5  absolute text-white cursor-pointer`} onClick={toggleSidebar} />
          : ""
          }
          <motion.button
            onClick={toggleSidebar}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-1 rounded-lg transition-all duration-300"
          >
            {!isOpen &&
              <FaBars className="w-8 h-8 bg-white rounded-lg p-2 text-[#2d2f36]" />
            }
          </motion.button>
        </div>

        {/* Navigation */}
        <nav className="space-y-6">
        {Object.entries(
            filteredNavigationItems.reduce((acc, item) => {
            const section = item.section || "Other";
            acc[section] = acc[section] || [];
            acc[section].push(item);
            return acc;
            }, {})
        ).map(([section, items], idx) => (
            <div key={idx} className="mt-4">
            {/* Section Title + Divider */}
            {isOpen && (
                <div className="flex items-center space-x-2 mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    {section}
                </span>
                <div className="flex-1 h-px bg-gray-600"></div>
                </div>
            )}

            {/* Section Items */}
            <div className="space-y-1">
                {items.map((item, index) => (
                <div key={index}>
                    <SidebarItem
                    icon={item.icon}
                    text={item.text}
                    to={item.to}
                    isOpen={isOpen}
                    hasDropdown={!!item.subItems}
                    isOpenDropdown={dropdownsOpen[item.text]}
                    onClick={() => toggleDropdown(item.text)}
                    />

                    {/* SubItems */}
                    <AnimatePresence>
                    {item.subItems && dropdownsOpen[item.text] && isOpen && (
                        <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`me-6 mt-1 space-y-1 ${currentLang === "ar" ? "border-l" : "border-r"}  border-gray-600 pe-3`}
                        >
                        {item.subItems.map((sub, subIndex) => (
                            <SidebarItem
                            key={subIndex}
                            icon={sub.icon}
                            text={sub.text}
                            to={sub.to}
                            isOpen={isOpen}
                            nested
                            />
                        ))}
                        </motion.div>
                    )}
                    </AnimatePresence>
                </div>
                ))}
            </div>
            </div>
        ))}
        </nav>

      </motion.div>
    </div>
  );
}

function SidebarItem({
  icon,
  text,
  to,
  isOpen,
  hasDropdown,
  isOpenDropdown,
  nested,
  onClick,
}) {
  const Component = to ? motion(Link) : motion.div;
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isActive = location.pathname === to;
  const currentLang = i18n.language;

  return (

<Component
  to={to}
  onClick={!to ? onClick : undefined}
  initial={{ scale: 0.95 }}
  animate={{ scale: 1 }}
  whileTap={{ scale: 0.98 }}
  transition={{ type: "spring", stiffness: 900 }}
  className={`group flex items-center ${
    isOpen ? "justify-between" : "justify-center"
  } relative p-3 rounded-xl transition-all duration-300 cursor-pointer
    ${
      isActive
        ? "text-white"
        : "text-gray-300 hover:text-secondary "
    }
    ${nested ? "ps-6 text-sm" : "text-base"}`}
>
  {/* Active background highlight (now rounded on the LEFT side) */}
  {isActive && isOpen && (
    <span
      className={`absolute ${currentLang === "ar" ? "left-2" : "right-2"} top-[4px] h-[84%] w-full ${currentLang === "ar" ? "bg-gradient-to-r rounded-l-3xl" : "bg-gradient-to-l rounded-r-3xl"} from-[#43938c] to-[#2F7A74]`}
      style={{ zIndex: 0 }}
    />
  )}

  <div className="flex items-center relative z-10">
    <span
      className={`p-2 rounded-md text-[16px] flex items-center justify-center transition-all duration-300
        ${
          isActive
            ? "bg-white/20 text-white"
            : "bg-transparent text-gray-400"
        }`}
    >
      {icon}
    </span>

    {isOpen && (
      <motion.span
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className={`mx-3 text-[15px] tracking-wide ${
          isActive ? "text-white font-medium" : "text-gray-300"
        } group-hover:text-white`}
      >
        {text}
      </motion.span>
    )}
  </div>

  {hasDropdown && isOpen && (
    <motion.span
      animate={{ rotate: isOpenDropdown ? (currentLang === "ar" ? 270 : 90) : 0 }}
      className={`ms-2 transition-colors relative z-10 ${
        isActive ? "text-white" : "text-gray-400"
      }`}
    >
      {currentLang === "ar" ? <FaChevronLeft /> : <FaChevronRight /> }
    </motion.span>
  )}
</Component>

  );
}
